import {
  createContext,
  useContext,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import { cn } from "./utils/cn";

interface TableContextValue {
  dense: boolean;
  zebra: boolean;
}
const TableContext = createContext<TableContextValue>({
  dense: false,
  zebra: false,
});

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  dense?: boolean;
  zebra?: boolean;
  /** Wrapper className (the bordered, rounded scroll container). */
  wrapperClassName?: string;
}

/**
 * Canonical semantic-HTML table. Composable parts share one look; `dense` and
 * `zebra` are boolean modifiers, not style variants. For sorting/virtualization
 * keep react-table for behavior and render its cells through these parts.
 */
function Table({
  dense = false,
  zebra = false,
  className,
  wrapperClassName,
  children,
  ...rest
}: TableProps) {
  return (
    <TableContext.Provider value={{ dense, zebra }}>
      <div
        className={cn(
          "overflow-x-auto rounded-card border border-border-subtle bg-surface",
          wrapperClassName
        )}
      >
        <table
          className={cn(
            "w-full min-w-full divide-y divide-border-subtle text-menu",
            className
          )}
          {...rest}
        >
          {children}
        </table>
      </div>
    </TableContext.Provider>
  );
}

function Head({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn("bg-surface-subtle", className)} {...rest} />;
}

function Body({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody
      className={cn("divide-y divide-border-subtle", className)}
      {...rest}
    />
  );
}

function Row({ className, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  const { zebra } = useContext(TableContext);
  return (
    <tr
      className={cn(
        "transition-colors duration-150 hover:bg-surface-subtle",
        zebra && "even:bg-surface-subtle/50",
        className
      )}
      {...rest}
    />
  );
}

function HeaderCell({
  className,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement>) {
  const { dense } = useContext(TableContext);
  return (
    <th
      scope="col"
      className={cn(
        "text-start text-xs font-medium tracking-normal text-menu/55",
        dense ? "px-3 py-2" : "px-5 py-3.5",
        className
      )}
      {...rest}
    />
  );
}

function Cell({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  const { dense } = useContext(TableContext);
  return (
    <td
      className={cn(
        "text-start align-middle",
        dense ? "px-3 py-2" : "px-5 py-3.5",
        className
      )}
      {...rest}
    />
  );
}

Table.Head = Head;
Table.Body = Body;
Table.Row = Row;
Table.HeaderCell = HeaderCell;
Table.Cell = Cell;

export default Table;
